import { supabase } from '@/lib/supabase'
import type { ImportRow, ParseResult } from './types'
import type { DiffResult } from './diffAgainstDb'

// -----------------------------------------------------------------------------
// Whitelist: columnas del "core actual" que el importer gestiona.
// Cualquier columna fuera de aqui (p.ej. auth_user_id, biometric_*, notas
// operativas de NeoCore) NO viaja en el payload, por lo tanto Supabase
// preserva su valor previo en el UPDATE on conflict.
// -----------------------------------------------------------------------------

type AsociadoUpsert = {
  no_asociado: string
  dpi: string
  nombre_completo: string
  genero: 'MASCULINO' | 'FEMENINO'
  profesion: string | null
  direccion: string | null
  municipio: string | null
  departamento: string | null
  telefono_e164: string | null
  usuario_core: string
  actividad_economica: string | null
  last_core_sync_at: string
}

type CuentaUpsert = {
  cuenta_id: string
  asociado_id: string
  no_asociado: string
  branch_code: string
  tipo_servicio: string
  pin_core: string | null
  fecha_apertura: string
  ultimo_movimiento: string | null
  fecha_vencimiento: string | null
  fecha_cierre: string | null
  cierre_anticipado: boolean
  saldo_disponible: number
  saldo_pignorado: number
  saldo_total: number
  estado: string
  last_core_sync_at: string
}

type BranchUpsert = {
  code: string
  name: string
  region_code: string | null
  region_name: string | null
}

// -----------------------------------------------------------------------------
// Utilidades
// -----------------------------------------------------------------------------

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
  return out
}

function rowToAsociado(row: ImportRow, now: string): AsociadoUpsert {
  return {
    no_asociado: row.no_asociado,
    dpi: row.dpi,
    nombre_completo: row.nombre_completo,
    genero: row.genero,
    profesion: row.profesion,
    direccion: row.direccion,
    municipio: row.municipio,
    departamento: row.departamento,
    telefono_e164: row.telefono_e164,
    usuario_core: row.usuario_core,
    actividad_economica: row.actividad_economica,
    last_core_sync_at: now,
  }
}

function rowToCuenta(row: ImportRow, asociadoId: string, now: string): CuentaUpsert {
  return {
    cuenta_id: row.cuenta_id,
    asociado_id: asociadoId,
    no_asociado: row.no_asociado,
    branch_code: row.branch_code,
    tipo_servicio: row.tipo_servicio,
    pin_core: row.pin_core,
    fecha_apertura: row.fecha_apertura,
    ultimo_movimiento: row.ultimo_movimiento,
    fecha_vencimiento: row.fecha_vencimiento,
    fecha_cierre: row.fecha_cierre,
    cierre_anticipado: row.cierre_anticipado,
    saldo_disponible: row.saldo_disponible,
    saldo_pignorado: row.saldo_pignorado,
    saldo_total: row.saldo_total,
    estado: row.estado,
    last_core_sync_at: now,
  }
}

// -----------------------------------------------------------------------------
// Apply
// -----------------------------------------------------------------------------

export type ApplyProgress = {
  phase:
    | 'starting'
    | 'branches'
    | 'asociados'
    | 'resolving_ids'
    | 'cuentas'
    | 'finalizing'
    | 'done'
    | 'failed'
  done: number
  total: number
  message?: string
}

export type ApplyResult = {
  runId: string
  status: 'ok' | 'partial' | 'failed'
  rowsOk: number
  rowsFail: number
  errorSummary: string[]
  finishedAt: string
}

export type ApplyOptions = {
  onProgress?: (p: ApplyProgress) => void
}

const BATCH = 100

export async function applyImport(
  parsed: ParseResult,
  diff: DiffResult,
  options: ApplyOptions = {},
): Promise<ApplyResult> {
  const progress = (p: ApplyProgress) => options.onProgress?.(p)
  const errors: string[] = []
  const now = new Date().toISOString()

  progress({ phase: 'starting', done: 0, total: parsed.rows.length })

  // 1) Crear registro en core_import_run
  const { data: userData } = await supabase.auth.getUser()
  const { data: runIns, error: runErr } = await supabase
    .from('core_import_run')
    .insert({
      archivo_nombre: parsed.fileName,
      archivo_hash: parsed.fileHash,
      uploaded_by: userData.user?.id ?? null,
      status: 'running',
      rows_total: parsed.rows.length,
    })
    .select('id')
    .single()
  if (runErr || !runIns) {
    throw new Error(`No pude crear core_import_run: ${runErr?.message ?? 'sin data'}`)
  }
  const runId = runIns.id as string

  try {
    // 2) Upsert branches (una sola pasada, pocos registros)
    progress({ phase: 'branches', done: 0, total: 1 })
    const branchMap = new Map<string, BranchUpsert>()
    for (const r of parsed.rows) {
      if (!branchMap.has(r.branch_code)) {
        branchMap.set(r.branch_code, {
          code: r.branch_code,
          name: r.branch_name ?? r.branch_code,
          region_code: r.region_code,
          region_name: r.region_name,
        })
      }
    }
    if (branchMap.size > 0) {
      const { error } = await supabase
        .from('branch')
        .upsert([...branchMap.values()], { onConflict: 'code' })
      if (error) throw new Error(`branch upsert: ${error.message}`)
    }

    // 3) Upsert asociados (unicos por no_asociado, whitelist)
    const asociadosUnicos = new Map<string, ImportRow>()
    for (const r of parsed.rows) {
      if (!asociadosUnicos.has(r.no_asociado)) asociadosUnicos.set(r.no_asociado, r)
    }
    const asociadoBatches = chunk([...asociadosUnicos.values()], BATCH)
    let asocDone = 0
    for (const batch of asociadoBatches) {
      const payload = batch.map((r) => rowToAsociado(r, now))
      const { error } = await supabase
        .from('asociado')
        .upsert(payload, { onConflict: 'no_asociado' })
      if (error) throw new Error(`asociado upsert: ${error.message}`)
      asocDone += batch.length
      progress({ phase: 'asociados', done: asocDone, total: asociadosUnicos.size })
    }

    // 4) Mapear no_asociado → asociado.id para poder insertar cuentas
    progress({
      phase: 'resolving_ids',
      done: 0,
      total: asociadosUnicos.size,
    })
    const noAsociadosList = [...asociadosUnicos.keys()]
    const idMap = new Map<string, string>()
    for (const slice of chunk(noAsociadosList, 500)) {
      const { data, error } = await supabase
        .from('asociado')
        .select('id, no_asociado')
        .in('no_asociado', slice)
      if (error) throw new Error(`resolver ids: ${error.message}`)
      for (const a of data ?? []) {
        idMap.set((a as { no_asociado: string }).no_asociado, (a as { id: string }).id)
      }
    }
    if (idMap.size !== asociadosUnicos.size) {
      errors.push(
        `Advertencia: solo resolví ${idMap.size} ids de ${asociadosUnicos.size} asociados. Algunas cuentas podrían fallar.`,
      )
    }

    // 5) Upsert cuentas (whitelist + asociado_id resuelto)
    const cuentaBatches = chunk(parsed.rows, BATCH)
    let cuentaDone = 0
    for (const batch of cuentaBatches) {
      const payload: CuentaUpsert[] = []
      for (const r of batch) {
        const asocId = idMap.get(r.no_asociado)
        if (!asocId) {
          errors.push(`Cuenta ${r.cuenta_id} sin asociado resuelto (${r.no_asociado}), se omite`)
          continue
        }
        payload.push(rowToCuenta(r, asocId, now))
      }
      if (payload.length === 0) continue
      const { error } = await supabase
        .from('cuenta')
        .upsert(payload, { onConflict: 'cuenta_id' })
      if (error) throw new Error(`cuenta upsert: ${error.message}`)
      cuentaDone += payload.length
      progress({ phase: 'cuentas', done: cuentaDone, total: parsed.rows.length })
    }

    // 6) Finalizar core_import_run con status ok / partial
    progress({ phase: 'finalizing', done: 1, total: 1 })
    const finishedAt = new Date().toISOString()
    const status: 'ok' | 'partial' = errors.length === 0 ? 'ok' : 'partial'
    const { error: upErr } = await supabase
      .from('core_import_run')
      .update({
        status,
        rows_total: parsed.rows.length,
        rows_ok: cuentaDone,
        rows_warn: parsed.warnings.length,
        rows_fail: errors.length,
        error_summary: errors.length > 0 ? { items: errors } : null,
        finished_at: finishedAt,
      })
      .eq('id', runId)
    if (upErr) {
      errors.push(`finalizar run: ${upErr.message}`)
    }

    progress({
      phase: 'done',
      done: parsed.rows.length,
      total: parsed.rows.length,
      message: status === 'ok' ? 'Import aplicado correctamente' : 'Import aplicado con advertencias',
    })

    void diff // usado en futuros pasos para snapshots/auditoria
    return {
      runId,
      status,
      rowsOk: cuentaDone,
      rowsFail: errors.length,
      errorSummary: errors,
      finishedAt,
    }
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : 'error desconocido'
    errors.push(msg)
    const finishedAt = new Date().toISOString()
    await supabase
      .from('core_import_run')
      .update({
        status: 'failed',
        error_summary: { items: errors },
        finished_at: finishedAt,
      })
      .eq('id', runId)
    progress({ phase: 'failed', done: 0, total: parsed.rows.length, message: msg })
    return {
      runId,
      status: 'failed',
      rowsOk: 0,
      rowsFail: errors.length,
      errorSummary: errors,
      finishedAt,
    }
  }
}

// -----------------------------------------------------------------------------
// Historial de runs
// -----------------------------------------------------------------------------

export type ImportRunLog = {
  id: string
  archivo_nombre: string
  archivo_hash: string
  status: 'pending' | 'running' | 'ok' | 'partial' | 'failed'
  rows_total: number | null
  rows_ok: number | null
  rows_warn: number | null
  rows_fail: number | null
  started_at: string
  finished_at: string | null
  uploaded_by_email: string | null
}

export async function fetchHistory(limit = 10): Promise<ImportRunLog[]> {
  const { data, error } = await supabase
    .from('core_import_run')
    .select(
      'id, archivo_nombre, archivo_hash, status, rows_total, rows_ok, rows_warn, rows_fail, started_at, finished_at',
    )
    .order('started_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return (data ?? []).map((r) => ({
    ...(r as ImportRunLog),
    uploaded_by_email: null, // en v0.2 joineamos con auth.users via vista
  }))
}
